// Customer detail + editor — basic identity, status, verification, and
// the `flagged` admin metadata. Editorial fields (email, name, phone,
// status, verified) write to cms.users; `flagged` writes to
// app.customer_profiles. Both inside one Hono PATCH transaction.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AvatarPanel } from '@/components/AvatarPanel';
import { DefRow, DetailLayout, DetailSection } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  TextField,
  SelectField,
  BooleanField,
  type SelectOption,
} from '@/components/forms';
import { env } from '@/lib/env';
import {
  adjustCustomerWallet,
  fetchAdminCustomerDetail,
  fetchAdminCustomerWallet,
  patchCustomer,
  suspendCustomer,
  unsuspendCustomer,
  type AdjustWalletInput,
  type AdminCustomerDetail,
  type AdminCustomerStatus,
  type AdminWalletTransaction,
  type CustomerPatchInput,
} from '@/lib/admin-api';

function resolveMediaUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(v);

const fmtCents = (cents: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);

const STATUS_OPTIONS: SelectOption<AdminCustomerStatus>[] = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

interface BookingRow {
  id: number;
  reference: string;
  status: string;
  total: number;
  currency: string;
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  carTitle: string | null;
  carSlug: string | null;
}

// ─── Editor draft ────────────────────────────────────────────────────

interface CustomerDraft {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: AdminCustomerStatus;
  verified: boolean;
  flagged: boolean;
}

function customerToDraft(c: AdminCustomerDetail): CustomerDraft {
  return {
    email: c.email,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    phone: c.phone ?? '',
    status: c.status as AdminCustomerStatus,
    verified: c.verified,
    flagged: c.profile?.flagged ?? false,
  };
}

function buildPatch(c: AdminCustomerDetail, d: CustomerDraft): CustomerPatchInput {
  const p: CustomerPatchInput = {};
  if (d.email.trim() !== c.email) p.email = d.email.trim();
  const firstName = d.firstName.trim() === '' ? null : d.firstName.trim();
  if (firstName !== c.firstName) p.firstName = firstName;
  const lastName = d.lastName.trim() === '' ? null : d.lastName.trim();
  if (lastName !== c.lastName) p.lastName = lastName;
  const phone = d.phone.trim() === '' ? null : d.phone.trim();
  if (phone !== c.phone) p.phone = phone;
  if (d.status !== c.status) p.status = d.status;
  if (d.verified !== c.verified) p.verified = d.verified;
  if (d.flagged !== (c.profile?.flagged ?? false)) p.flagged = d.flagged;
  return p;
}

// ─── Page ────────────────────────────────────────────────────────────

export function CustomerDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'customers', id],
    queryFn: ({ signal }) => fetchAdminCustomerDetail(id, signal),
  });

  const [draft, setDraft] = useState<CustomerDraft | null>(null);
  useEffect(() => {
    if (data) setDraft(customerToDraft(data));
  }, [data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
  };

  const suspendMut = useMutation({
    mutationFn: ({ id: i, reason }: { id: number; reason?: string }) =>
      suspendCustomer(i, reason),
    onSuccess: invalidate,
  });
  const unsuspendMut = useMutation({ mutationFn: unsuspendCustomer, onSuccess: invalidate });
  const saveMut = useMutation({
    mutationFn: (body: CustomerPatchInput) => patchCustomer(id, body),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'customers', id] });
    },
  });

  if (isLoading || !draft) {
    return (
      <DetailLayout title="Loading…" backTo="/customers" backLabel="All customers">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading customer…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/customers" backLabel="All customers">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No customer with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const c = data;
  const display = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email;
  const moderationBusy = suspendMut.isPending || unsuspendMut.isPending;

  const patch = buildPatch(c, draft);
  const dirty = Object.keys(patch).length > 0;

  const setField = <K extends keyof CustomerDraft>(k: K, val: CustomerDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: val } : d));

  const bookingCols: DataTableColumn<BookingRow>[] = [
    { key: 'reference', h: 'Code', mono: true },
    { key: 'car', h: 'Car', cell: (r) => r.carTitle ?? r.carSlug ?? '—' },
    {
      key: 'pickup',
      h: 'Pickup',
      cell: (r) => new Date(r.dateFrom).toLocaleDateString(),
    },
    {
      key: 'total',
      h: 'Total',
      right: true,
      cell: (r) => <span className="tabular-nums">{fmtMoney(r.total, r.currency)}</span>,
    },
    { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  ];

  return (
    <DetailLayout
      eyebrow="Operate · Customer"
      title={display}
      sub={`${c.email} · ${c.bookingsTotal} bookings${
        c.profile ? ` · ${fmtCents(c.profile.totalSpentCents)} lifetime` : ''
      }`}
      backTo="/customers"
      backLabel="All customers"
      actions={
        <>
          {c.status === 'suspended' ? (
            <RowAction busy={moderationBusy} onClick={() => unsuspendMut.mutate(c.id)}>
              Unsuspend
            </RowAction>
          ) : (
            <RowAction
              kind="danger"
              busy={moderationBusy}
              onClick={() => {
                const reason = window.prompt('Suspension reason (optional):') ?? undefined;
                suspendMut.mutate({ id: c.id, reason });
              }}
            >
              Suspend
            </RowAction>
          )}
          <StatusPill status={c.status} />
        </>
      }
    >
      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(customerToDraft(c))}
        onSave={() => saveMut.mutate(patch)}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DetailSection title="Identity">
          <AvatarPanel
            base="customers"
            userId={c.id}
            display={display}
            joinedAt={c.createdAt}
            avatarUrl={resolveMediaUrl(c.avatar?.url)}
          />
          <div className="space-y-4">
            <TextField
              label="Email"
              required
              type="email"
              value={draft.email}
              onChange={(val) => setField('email', val)}
            />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="First name"
                value={draft.firstName}
                onChange={(val) => setField('firstName', val)}
              />
              <TextField
                label="Last name"
                value={draft.lastName}
                onChange={(val) => setField('lastName', val)}
              />
            </div>
            <TextField
              label="Phone"
              type="tel"
              value={draft.phone}
              onChange={(val) => setField('phone', val)}
            />
          </div>
        </DetailSection>

        <DetailSection title="Status & trust">
          <div className="space-y-4">
            <SelectField
              label="Account status"
              required
              value={draft.status}
              onChange={(val) => setField('status', val)}
              options={STATUS_OPTIONS}
              hint="`suspended` blocks login + new bookings. The Suspend / Unsuspend buttons above are shortcuts."
            />
            <BooleanField
              label="Email verified"
              hint="Set on signup confirmation. Manual override sometimes needed when a verification email gets lost."
              checked={draft.verified}
              onChange={(val) => setField('verified', val)}
            />
            <BooleanField
              label="Flagged for review"
              hint="Internal flag — surfaces in the customers list when filtering by `flagged`."
              checked={draft.flagged}
              onChange={(val) => setField('flagged', val)}
            />
          </div>
        </DetailSection>

        <DetailSection title="Activity (read-only)">
          <DefRow label="Bookings">{c.bookingsTotal}</DefRow>
          <DefRow label="Trips completed">{c.profile?.tripsCount ?? 0}</DefRow>
          <DefRow label="Lifetime spend">
            {c.profile ? fmtCents(c.profile.totalSpentCents) : '—'}
          </DefRow>
          <DefRow label="Last seen">
            {c.profile?.lastActiveAt
              ? new Date(c.profile.lastActiveAt).toLocaleDateString()
              : '—'}
          </DefRow>
        </DetailSection>

        <DetailSection title="Trust (read-only)">
          <DefRow label="Lifetime rating">
            {c.profile?.lifetimeRating == null ? '—' : c.profile.lifetimeRating.toFixed(2)}
          </DefRow>
        </DetailSection>
      </div>

      <DetailSection title="Recent bookings">
        <DataTable<BookingRow>
          columns={bookingCols}
          rows={c.recentBookings}
          onRowClick={(r) =>
            void navigate({
              to: '/bookings/$reference' as never,
              params: { reference: r.reference } as never,
            })
          }
          emptyLabel="No bookings yet."
        />
      </DetailSection>

      <WalletSection customerId={c.id} />

      <EntityActivity entityType="users" entityId={c.id} title="Audit trail" />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(customerToDraft(c))}
        onSave={() => saveMut.mutate(patch)}
      />
    </DetailLayout>
  );
}

// ─── Save bar ───────────────────────────────────────────────────────

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Wallet ─────────────────────────────────────────────────────────
//
// Reads /admin/customers/:id/wallet and renders the balance, recent
// ledger, and a credit/debit adjuster. Mutations write via the same
// creditWallet/debitWallet helpers the customer-facing flows use, so a
// goodwill credit booked here behaves identically to a Stripe refund or
// a booking refund — same ledger column, same balance update.

const TXN_LABEL: Record<AdminWalletTransaction['type'], string> = {
  topup: 'Top-up',
  debit: 'Booking debit',
  refund: 'Refund',
  adjust: 'Admin adjust',
};

const TXN_COLUMNS: DataTableColumn<AdminWalletTransaction>[] = [
  {
    key: 'createdAt',
    h: 'When',
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {new Date(r.createdAt).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'type',
    h: 'Type',
    cell: (r) => <span>{TXN_LABEL[r.type]}</span>,
  },
  {
    key: 'reference',
    h: 'Reference',
    cell: (r) => <span className="font-mono text-[11.5px]">{r.reference}</span>,
  },
  {
    key: 'amount',
    h: 'Amount',
    right: true,
    cell: (r) => {
      // Credits (positive in absolute terms) come in as topup/refund/adjust;
      // debits as debit/adjust. The amount is always magnitude — we colour
      // by sign of intent.
      const isCredit = r.type === 'topup' || r.type === 'refund';
      const isDebit = r.type === 'debit';
      // 'adjust' can be either; sign is hidden in the audit. The Hono module
      // stamps the action distinguishing credit/debit on the audit row.
      // For the ledger we show the absolute amount + symbol if known.
      const color = isCredit
        ? 'text-[#166534]'
        : isDebit
          ? 'text-[#9f1239]'
          : 'text-[color:var(--bo-ink)]';
      const sign = isCredit ? '+' : isDebit ? '−' : '±';
      return (
        <span className={`tabular-nums font-semibold ${color}`}>
          {sign}
          {fmtMoney(r.amount, r.currency)}
        </span>
      );
    },
  },
  {
    key: 'balanceAfter',
    h: 'Balance after',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">{fmtMoney(r.balanceAfter, r.currency)}</span>
    ),
  },
  {
    key: 'note',
    h: 'Note',
    cell: (r) => (
      <span className="line-clamp-1 max-w-[280px] text-[12px] text-[color:var(--bo-mute)]">
        {r.note ?? (r.bookingId ? `Booking #${r.bookingId}` : '—')}
      </span>
    ),
  },
];

function WalletSection({ customerId }: { customerId: number }) {
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<AdjustWalletInput['direction']>('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'customers', customerId, 'wallet'],
    queryFn: ({ signal }) => fetchAdminCustomerWallet(customerId, signal),
  });

  const adjustMut = useMutation({
    mutationFn: (input: AdjustWalletInput) => adjustCustomerWallet(customerId, input),
    onSuccess: () => {
      setAmount('');
      setNote('');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'customers', customerId, 'wallet'],
      });
    },
  });

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const trimmedNote = note.trim();
  const canSubmit = amountValid && trimmedNote.length > 0 && !adjustMut.isPending;
  const currency = data?.currency ?? 'EUR';

  return (
    <DetailSection title="Wallet">
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load wallet: {(error as Error).message}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <span className="text-[11.5px] uppercase tracking-[0.08em] text-[color:var(--bo-mute)]">
          Balance
        </span>
        <span className="text-[22px] font-semibold tabular-nums text-[color:var(--bo-ink)]">
          {isLoading ? '…' : fmtMoney(data?.balance ?? 0, currency)}
        </span>
        <span className="text-[11.5px] text-[color:var(--bo-mute)]">{currency}</span>
      </div>

      <div className="mb-5 rounded-xl border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]/40 p-4">
        <div className="mb-3 text-[12.5px] font-semibold text-[color:var(--bo-ink)]">
          Adjust balance
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[120px_140px_1fr_auto]">
          <select
            value={direction}
            onChange={(e) =>
              setDirection(e.target.value as AdjustWalletInput['direction'])
            }
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          >
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder={`Amount in ${currency}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-2 text-[13px] tabular-nums text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          />
          <input
            type="text"
            placeholder="Reason (required) — visible in audit log"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          />
          <RowAction
            kind={direction === 'credit' ? 'primary' : 'danger'}
            busy={adjustMut.isPending}
            disabled={!canSubmit}
            onClick={() => {
              if (!canSubmit) return;
              adjustMut.mutate({
                direction,
                amount: parsedAmount,
                note: trimmedNote,
              });
            }}
          >
            {direction === 'credit' ? 'Credit wallet' : 'Debit wallet'}
          </RowAction>
        </div>
        {adjustMut.error ? (
          <div className="mt-2 text-[12px] text-[#9f1239]">
            Adjustment failed: {(adjustMut.error as Error).message}
          </div>
        ) : null}
        {adjustMut.isSuccess && !adjustMut.isPending ? (
          <div className="mt-2 text-[12px] text-[#166534]">
            Wallet updated. New balance:{' '}
            <span className="tabular-nums font-semibold">
              {fmtMoney(adjustMut.data?.balanceAfter ?? 0, currency)}
            </span>
          </div>
        ) : null}
      </div>

      <DataTable<AdminWalletTransaction>
        columns={TXN_COLUMNS}
        rows={data?.transactions ?? []}
        emptyLabel={isLoading ? 'Loading…' : 'No wallet activity yet.'}
      />
    </DetailSection>
  );
}
